import { G } from '@mobily/ts-belt';
import { fetchPropertyConnection, propertyConnectionKey } from 'app/areas/hooks/useGetPropertyConnections';
import { fetchOutlets, outletsKey } from 'app/outlets/hooks/useOutlets';
import { fetchPremiseById, premiseByIdKey } from 'app/premises/hooks/usePremise';
import { fetchPremises, allPremisesKey } from 'app/premises/hooks/usePremisesList';
import { fetchPropertyById, propertyByIdKey } from 'app/properties/hooks/useProperty';
import { createPrefetchSession, PrefetchSession } from 'common/offline/prefetchSession';
import { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  fetchPropertyRadioConnection,
  propertyRadioConnectionKey,
} from 'app/areas/hooks/useGetPropertyRadioConnections';
import { fetchWorkSessionByPropertyId, workSessionByPropertyIdKey } from './useActiveWorkSession';
import { prefetchImages } from './prefetchImages';

export const prefetchPremise = async (session: PrefetchSession, id: number) => {
  return session.prefetchQuery(premiseByIdKey(id), fetchPremiseById(id));
};

export const prefetchPremises = async (session: PrefetchSession, propertyId: number) => {
  const premisesResponse = await session.fetchQuery(allPremisesKey(propertyId), fetchPremises(propertyId));

  const premises = premisesResponse.allPremises?.nodes ?? [];
  const premiseIds = premises.map((p) => p.id);

  return Promise.allSettled(premiseIds.map((id) => prefetchPremise(session, id)));
};

export const prefetchAreas = async (session: PrefetchSession, propertyId: number) => {
  const connections = await session.fetchQuery(propertyConnectionKey(propertyId), fetchPropertyConnection(propertyId));
  const radioConnections = await session.fetchQuery(
    propertyRadioConnectionKey(propertyId),
    fetchPropertyRadioConnection(propertyId),
  );

  const connectionImages =
    connections.propertyById.connectionByConnectionId?.serialized2.nodeDataArray.flatMap(
      (node) => node.cfObject?.images ?? [],
    ) ?? [];

  const radioConnectionsImages =
    radioConnections.propertyById.radioConnectionByRadioConnectionId?.serialized2.nodeDataArray.flatMap(
      (node) => node.cfObject?.images ?? [],
    ) ?? [];

  const images = prefetchImages([...connectionImages, ...radioConnectionsImages], {
    concurrency: 6,
    credentials: 'include',
  });

  return Promise.allSettled([connections, radioConnections, images]);
};

export const prefetchPropertyData = async (session: PrefetchSession, propertyId: number) => {
  const workSession = session.fetchQuery(
    workSessionByPropertyIdKey(propertyId),
    fetchWorkSessionByPropertyId(propertyId),
  );

  const property = session.prefetchQuery(propertyByIdKey(propertyId), fetchPropertyById(propertyId));
  const premises = prefetchPremises(session, propertyId);
  const outlets = session.prefetchQuery(outletsKey(propertyId), fetchOutlets(propertyId));
  const areas = prefetchAreas(session, propertyId);

  const results = await Promise.allSettled([workSession, property, premises, outlets, areas]);
  const hasFailures = results.some((r) => r.status === 'rejected');

  return { hasFailures };
};

export const useManualCacheData = (propertyId: number | null) => {
  const qc = useQueryClient();
  const [isCaching, setIsCaching] = useState(false);

  const cacheData = useCallback(async () => {
    if (G.isNullable(propertyId)) return { hasFailures: false };

    setIsCaching(true);
    try {
      const session = createPrefetchSession(qc);
      const result = await prefetchPropertyData(session, propertyId);
      await session.persist();
      return result;
    } finally {
      setIsCaching(false);
    }
  }, [propertyId, qc]);

  return { cacheData, isCaching };
};
